import * as Sentry from "@sentry/remix";

export interface AuthTelemetryContext {
  ua: string;
  inAppBrowser: boolean;
  inAppBrowserName: string | null;
  platform: "ios" | "android" | "desktop";
  hasSessionCookie: boolean;
  hasSupabaseStorage: boolean;
  cookieEnabled: boolean;
}

const IN_APP_PATTERNS: Array<[RegExp, string]> = [
  [/Slack/i, "slack"],
  [/Discord/i, "discord"],
  [/FBAN|FBAV/i, "facebook"],
  [/Instagram/i, "instagram"],
  [/Line\//i, "line"],
  [/KAKAOTALK/i, "kakaotalk"],
];

export function getAuthContext(): AuthTelemetryContext {
  if (typeof window === "undefined") {
    return {
      ua: "",
      inAppBrowser: false,
      inAppBrowserName: null,
      platform: "desktop",
      hasSessionCookie: false,
      hasSupabaseStorage: false,
      cookieEnabled: false,
    };
  }

  const ua = navigator.userAgent;
  const match = IN_APP_PATTERNS.find(([pattern]) => pattern.test(ua));
  const platform: AuthTelemetryContext["platform"] = /iPhone|iPad|iPod/.test(ua)
    ? "ios"
    : /Android/.test(ua)
      ? "android"
      : "desktop";

  let hasSupabaseStorage = false;
  try {
    hasSupabaseStorage = Object.keys(localStorage).some((k) => k.startsWith("sb-"));
  } catch {
    // localStorage may be blocked in in-app browsers
  }

  return {
    ua,
    inAppBrowser: !!match,
    inAppBrowserName: match?.[1] ?? null,
    platform,
    hasSessionCookie: document.cookie.includes("__session"),
    hasSupabaseStorage,
    cookieEnabled: navigator.cookieEnabled,
  };
}

export function captureAuthEvent(
  name: string,
  level: "info" | "warning" | "error" = "warning",
  extra?: Record<string, unknown>,
) {
  const ctx = getAuthContext();
  Sentry.captureMessage(name, {
    level,
    tags: {
      auth_event: name,
      in_app_browser: String(ctx.inAppBrowser),
      in_app_name: ctx.inAppBrowserName ?? "none",
      platform: ctx.platform,
    },
    extra: { ...ctx, ...extra },
  });
}

export function addAuthBreadcrumb(message: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    category: "auth",
    message,
    level: "info",
    data: { ...getAuthContext(), ...data },
  });
}
